import { CreateRadioStationRequest, CreateRadioStationResponse, RadioStation } from '@/types/model';
import { apiClient, QueryParams, PaginatedResponse } from './apiClient';

export interface RadioStationQueryParams extends QueryParams {
  active?: boolean;
  country?: string;
  'country.id'?: string;
  genres?: string[];
  categories?: string[];
}

export const radioStationService = {
  // Fetch every page so campaign targeting is not limited to the first page.
  async getRadioStationsForDropdown(): Promise<RadioStation[]> {
    const stations: RadioStation[] = [];
    let page = 0;
    let totalPages = 1;
    do {
      const response = await radioStationService.getRadioStations({ _page: page, _size: 100 });
      stations.push(...response.content);
      totalPages = response.totalPages;
      page++;
    } while (page < totalPages);
    return stations;
  },

  /**
   * Get radio stations with pagination, search, and filtering
   */
  async getRadioStations(params: RadioStationQueryParams = {}): Promise<PaginatedResponse<RadioStation>> {
    return apiClient.getPaginated<RadioStation>('/radio-stations', params);
  },

  /**
   * Get a single radio station by ID
   */
  async getRadioStationById(id: string): Promise<RadioStation> {
    return apiClient.getById<RadioStation>('/radio-stations', id);
  },

  /**
   * Create a new radio station
   */
  async createRadioStation(radioStationData: CreateRadioStationRequest): Promise<CreateRadioStationResponse> {
    return apiClient.create<CreateRadioStationRequest, CreateRadioStationResponse>('/radio-stations', radioStationData);
  },

  /**
   * Update a radio station
   */
  async updateRadioStation(id: string, radioStationData: Partial<CreateRadioStationRequest>): Promise<CreateRadioStationResponse> {
    return apiClient.update<Partial<CreateRadioStationRequest>, CreateRadioStationResponse>('/radio-stations', id, radioStationData);
  },

  /**
   * Delete a radio station
   */
  async deleteRadioStation(id: string): Promise<{ success: boolean; message: string }> {
    return apiClient.deleteById<{ success: boolean; message: string }>('/radio-stations', id);
  },

  /**
   * Toggle radio station active status
   */
  async toggleRadioStationStatus(id: string, active: boolean): Promise<CreateRadioStationResponse> {
    return apiClient.toggleStatus<CreateRadioStationResponse>('/radio-stations', id, active);
  },
};
